package modysy.tools

import modysy.view.Color
import org.scalajs.dom

import scala.collection.mutable.ListBuffer

/** This class provides a balance of some energy points with an interface to
  * add or remove some energy. Any account movements and the current balance
  * can be rendered with render(...). The rendering is intended to show
  * only recent movement, so call updateAnimation() before every call to render(...)
  * to make obsolete movements disappear once they are outdated.
  */
class EnergyStorage {

  private case class Movement(amount: Int, var animationTTL: Int)

  private val MOVEMENT_VERTICAL_SPACE = 25
  private val MOVEMENT_BOTTOM_SPACE = 45
  private val MOVEMENT_INITIAL_TTL = 120

  private var _balance: Int = 0
  private val movements: ListBuffer[Movement] = new ListBuffer[Movement]

  /** Returns the current balance.
    *
    * @return the current balance
    */
  def balance: Int = _balance

  /** Adds the given, non-negative amount of energy to the EnergyStorage.
    * This account movement is added for positive amounts.
    *
    * @param amount the positive amount of energy to be added
    */
  def addEnergy(amount: Int): Unit = {
    require(amount >= 0)
    _balance += amount
    if (amount > 0) {
      movements.append(Movement(amount, MOVEMENT_INITIAL_TTL))
    }
  }

  /** Removes the given, non-negative amount of energy from the EnergyStorage.
    * The amount must be equal or less than the current balance
    * as the balance must stay non-negative.
    *
    * This account movement is added for positive amounts.
    *
    * @param amount the positive amount of energy to be removed
    */
  def removeEnergy(amount: Int): Unit = {
    require(_balance >= amount && amount >= 0)
    _balance -= amount
    if (amount > 0) {
      movements.append(Movement(-amount, MOVEMENT_INITIAL_TTL))
    }
  }

  /** Resets the EnergyStorage to the given, non-negative amount of
    * energy. All account movements are removed. No account movement
    * is added.
    *
    * @param balance the new, non-negative amount of energy
    */
  def reset(balance: Int): Unit = {
    require(balance >= 0)
    _balance = balance
    movements.clear()
  }

  /** Renders the current balance and the last four account movements starting
    * at (0, 0) moving to the left and upwards onto the given rendering context.
    *
    * The context font, fillStyle and textAlign is modified.
    *
    * @param ctx the rendering context to render on
    */
  def render(ctx: dom.CanvasRenderingContext2D): Unit = {
    // render current balance
    ctx.font = "45px Arial"
    ctx.fillStyle = Color.Gold
    ctx.textAlign = "right"
    ctx.fillText(_balance.toString, 125, 0, 125)

    ctx.font = "20px Arial"

    // render the last four movements
    Seq(0, 1, 2, 3).foreach(index => {
      if (movements.length > index) {
        if (movements(index).amount > 0) {
          ctx.fillStyle = Color.Green
          ctx.fillText(
            "+" + movements(index).amount.toString,
            125,
            -MOVEMENT_BOTTOM_SPACE - index * MOVEMENT_VERTICAL_SPACE,
            125
          )
        } else {
          ctx.fillStyle = Color.Red
          ctx.fillText(
            movements(index).amount.toString,
            125,
            -MOVEMENT_BOTTOM_SPACE - index * MOVEMENT_VERTICAL_SPACE,
            125
          )
        }
      }
    })

    // render three points to indicate more movements exist, but are not shown
    if (movements.length > 4) {
      ctx.fillStyle = Color.White
      ctx.fillText(
        "...",
        125,
        -MOVEMENT_BOTTOM_SPACE - 4 * MOVEMENT_VERTICAL_SPACE,
        125
      )
    }
  }

  /** Lowers the remaining time account movements are rendered.
    * Call this before every call to render(...).
    */
  def updateAnimation(): Unit = {
    // TODO ugly code, so sad
    movements.foreach(elem => elem.animationTTL = elem.animationTTL - 1)
    movements --= movements.filter(elem => elem.animationTTL <= 0)
  }
}



// WEBPACK FOOTER //
// /home/gitlab-runner/builds/2d4fb5cd/0/mtv-pr-2018/modysy-product/src/main/scala/modysy/tools/EnergyStorage.scala